import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../../services/api';

const StressEventsEditor = () => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({ event_name: '', start_date: '', end_date: '', description: '' });

  // Private Market Betas state
  const [betas, setBetas] = useState([]);
  const [betasLoading, setBetasLoading] = useState(false);
  const [betaMessage, setBetaMessage] = useState(null);
  const [editingBeta, setEditingBeta] = useState(null);
  const [betaForm, setBetaForm] = useState({
    asset_class: '', intercept: 0, beta_bnd_us: 0, beta_eq_world: 0, beta_credit_gl: 0
  });
  const [seedingBetas, setSeedingBetas] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchBetas();
  }, []);

  // ── Stress Events ──────────────────────────────────────────────────

  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const data = await adminAPI.getStressEvents();
      setEvents(data);
    } catch (err) {
      console.error('Error fetching stress events:', err);
      setMessage({ type: 'error', text: 'Failed to load stress events' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      const result = await adminAPI.seedStressEvents();
      setMessage({ type: 'success', text: result.message });
      fetchEvents();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to seed events' });
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event.id);
    setFormData({
      event_name: event.event_name,
      start_date: event.start_date,
      end_date: event.end_date,
      description: event.description || '',
    });
  };

  const handleAdd = () => {
    setEditingEvent('new');
    setFormData({ event_name: '', start_date: '', end_date: '', description: '' });
  };

  const handleCancel = () => {
    setEditingEvent(null);
    setFormData({ event_name: '', start_date: '', end_date: '', description: '' });
  };

  const handleSave = async () => {
    if (!formData.event_name || !formData.start_date || !formData.end_date) {
      setMessage({ type: 'error', text: 'Name, start date and end date are required' });
      return;
    }
    try {
      if (editingEvent === 'new') {
        await adminAPI.createStressEvent(formData);
        setMessage({ type: 'success', text: 'Event created' });
      } else {
        await adminAPI.updateStressEvent(editingEvent, formData);
        setMessage({ type: 'success', text: 'Event updated' });
      }
      setEditingEvent(null);
      setFormData({ event_name: '', start_date: '', end_date: '', description: '' });
      fetchEvents();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save event' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this stress event?')) return;
    try {
      await adminAPI.deleteStressEvent(id);
      setMessage({ type: 'success', text: 'Event deleted' });
      fetchEvents();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete event' });
    }
  };

  // ── Private Market Betas ───────────────────────────────────────────

  const fetchBetas = async () => {
    setBetasLoading(true);
    try {
      const data = await adminAPI.getPrivateMarketBetas();
      setBetas(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching betas:', err);
      setBetaMessage({ type: 'error', text: 'Failed to load private market betas' });
    } finally {
      setBetasLoading(false);
    }
  };

  const handleBetaAdd = () => {
    setEditingBeta('new');
    setBetaForm({ asset_class: '', intercept: 0, beta_bnd_us: 0, beta_eq_world: 0, beta_credit_gl: 0 });
  };

  const handleBetaEdit = (beta) => {
    setEditingBeta(beta.id);
    setBetaForm({
      asset_class: beta.asset_class,
      intercept: beta.intercept,
      beta_bnd_us: beta.beta_bnd_us,
      beta_eq_world: beta.beta_eq_world,
      beta_credit_gl: beta.beta_credit_gl,
    });
  };

  const handleBetaCancel = () => {
    setEditingBeta(null);
    setBetaForm({ asset_class: '', intercept: 0, beta_bnd_us: 0, beta_eq_world: 0, beta_credit_gl: 0 });
  };

  const handleBetaSave = async () => {
    if (!betaForm.asset_class) {
      setBetaMessage({ type: 'error', text: 'Asset class is required' });
      return;
    }
    try {
      if (editingBeta === 'new') {
        await adminAPI.createPrivateMarketBeta(betaForm);
        setBetaMessage({ type: 'success', text: 'Beta created' });
      } else {
        await adminAPI.updatePrivateMarketBeta(editingBeta, betaForm);
        setBetaMessage({ type: 'success', text: 'Beta updated' });
      }
      setEditingBeta(null);
      setBetaForm({ asset_class: '', intercept: 0, beta_bnd_us: 0, beta_eq_world: 0, beta_credit_gl: 0 });
      fetchBetas();
    } catch (err) {
      setBetaMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save beta' });
    }
  };

  const handleBetaDelete = async (id) => {
    if (!window.confirm('Delete this private market beta?')) return;
    try {
      await adminAPI.deletePrivateMarketBeta(id);
      setBetaMessage({ type: 'success', text: 'Beta deleted' });
      fetchBetas();
    } catch (err) {
      setBetaMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete beta' });
    }
  };

  const handleSeedBetas = async () => {
    setSeedingBetas(true);
    setBetaMessage(null);
    try {
      const result = await adminAPI.seedPrivateMarketBetas();
      setBetaMessage({ type: 'success', text: result.message });
      fetchBetas();
    } catch (err) {
      setBetaMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to compute betas' });
    } finally {
      setSeedingBetas(false);
    }
  };

  return (
    <>
      {/* Stress Events Card */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Stress Events</h5>
          <div>
            {events.length === 0 && (
              <button className="btn btn-outline-secondary btn-sm me-2" onClick={handleSeed}>
                Seed Defaults
              </button>
            )}
            <button className="btn btn-sm" style={{ backgroundColor: 'var(--secondary-color)', color: '#fff' }} onClick={handleAdd}>
              + Add Event
            </button>
          </div>
        </div>
        <div className="card-body">
          {message && (
            <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible fade show`}>
              {message.text}
              <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm" role="status"></div>
              <span className="ms-2">Loading...</span>
            </div>
          ) : (
            <>
              {/* Add / Edit form */}
              {editingEvent !== null && (
                <div className="card mb-3" style={{ borderColor: 'var(--input-border)' }}>
                  <div className="card-body">
                    <h6>{editingEvent === 'new' ? 'Add Stress Event' : 'Edit Stress Event'}</h6>
                    <div className="row g-2 mb-2">
                      <div className="col-md-4">
                        <label className="form-label small">Event Name</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formData.event_name}
                          onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">Start Date</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={formData.start_date}
                          onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">End Date</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={formData.end_date}
                          onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Description</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      />
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm" style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }} onClick={handleSave}>
                        Save
                      </button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={handleCancel}>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Events table */}
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Event Name</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Description</th>
                      <th style={{ width: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-3">
                          No stress events configured. Click "Seed Defaults" to add the standard events.
                        </td>
                      </tr>
                    ) : (
                      events.map((event) => (
                        <tr key={event.id}>
                          <td>{event.event_name}</td>
                          <td>{event.start_date}</td>
                          <td>{event.end_date}</td>
                          <td>{event.description}</td>
                          <td>
                            <button
                              className="btn btn-outline-primary btn-sm me-1"
                              onClick={() => handleEdit(event)}
                              title="Edit"
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDelete(event.id)}
                              title="Delete"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Private Market Betas Card */}
      <div className="card mt-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Private Market Betas</h5>
          <div>
            <button
              className="btn btn-outline-secondary btn-sm me-2"
              onClick={handleSeedBetas}
              disabled={seedingBetas}
            >
              {seedingBetas ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                  Computing...
                </>
              ) : (
                'Calculate from Data'
              )}
            </button>
            <button className="btn btn-sm" style={{ backgroundColor: 'var(--secondary-color)', color: '#fff' }} onClick={handleBetaAdd}>
              + Add Beta
            </button>
          </div>
        </div>
        <div className="card-body">
          <p className="text-muted small mb-3">
            3-factor regression model: <code>Return(t) = intercept + &beta;<sub>BND_US</sub> &times; BND_US(t) + &beta;<sub>EQ_WORLD</sub> &times; EQ_WORLD(t) + &beta;<sub>CREDIT_GL</sub> &times; CREDIT_GL(t)</code>
            <br />
            These betas synthesise monthly private market returns from listed-market factors for stress testing.
          </p>

          {betaMessage && (
            <div className={`alert alert-${betaMessage.type === 'error' ? 'danger' : 'success'} alert-dismissible fade show`}>
              {betaMessage.text}
              <button type="button" className="btn-close" onClick={() => setBetaMessage(null)}></button>
            </div>
          )}

          {betasLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm" role="status"></div>
              <span className="ms-2">Loading...</span>
            </div>
          ) : (
            <>
              {/* Add / Edit beta form */}
              {editingBeta !== null && (
                <div className="card mb-3" style={{ borderColor: 'var(--input-border)' }}>
                  <div className="card-body">
                    <h6>{editingBeta === 'new' ? 'Add Private Market Beta' : 'Edit Private Market Beta'}</h6>
                    <div className="row g-2 mb-2">
                      <div className="col-md-3">
                        <label className="form-label small">Asset Class</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={betaForm.asset_class}
                          onChange={(e) => setBetaForm({ ...betaForm, asset_class: e.target.value })}
                          placeholder="e.g. PRIVATE_EQUITY"
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small">Intercept</label>
                        <input
                          type="number"
                          step="any"
                          className="form-control form-control-sm"
                          value={betaForm.intercept}
                          onChange={(e) => setBetaForm({ ...betaForm, intercept: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small">&beta; BND_US</label>
                        <input
                          type="number"
                          step="any"
                          className="form-control form-control-sm"
                          value={betaForm.beta_bnd_us}
                          onChange={(e) => setBetaForm({ ...betaForm, beta_bnd_us: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small">&beta; EQ_WORLD</label>
                        <input
                          type="number"
                          step="any"
                          className="form-control form-control-sm"
                          value={betaForm.beta_eq_world}
                          onChange={(e) => setBetaForm({ ...betaForm, beta_eq_world: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small">&beta; CREDIT_GL</label>
                        <input
                          type="number"
                          step="any"
                          className="form-control form-control-sm"
                          value={betaForm.beta_credit_gl}
                          onChange={(e) => setBetaForm({ ...betaForm, beta_credit_gl: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm" style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }} onClick={handleBetaSave}>
                        Save
                      </button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={handleBetaCancel}>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Betas table */}
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Asset Class</th>
                      <th>Intercept</th>
                      <th>&beta; BND_US</th>
                      <th>&beta; EQ_WORLD</th>
                      <th>&beta; CREDIT_GL</th>
                      <th style={{ width: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {betas.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-3">
                          No private market betas configured. Click "Calculate from Data" to auto-compute from historical returns.
                        </td>
                      </tr>
                    ) : (
                      betas.map((beta) => (
                        <tr key={beta.id}>
                          <td><code>{beta.asset_class}</code></td>
                          <td>{beta.intercept?.toFixed(6)}</td>
                          <td>{beta.beta_bnd_us?.toFixed(4)}</td>
                          <td>{beta.beta_eq_world?.toFixed(4)}</td>
                          <td>{beta.beta_credit_gl?.toFixed(4)}</td>
                          <td>
                            <button
                              className="btn btn-outline-primary btn-sm me-1"
                              onClick={() => handleBetaEdit(beta)}
                              title="Edit"
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleBetaDelete(beta.id)}
                              title="Delete"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default StressEventsEditor;
